/**
 * **Add time for the crew** — `docs/visit-page.md` §27.4; Board 18 E (popup
 * 520). Board 7's **Add time** opens it in both modes: while the crew app can
 * punch (`crew.punch` on) it is the office adding a day somebody could not
 * punch, and while it cannot it is how a day's time reaches the visit at all.
 *
 * **Person** · **Day** (required); **Clocked in** *at …* · **Arrived** ·
 * **Left** · **Clocked out** *at …*; **Lunch from** · **Lunch to**; the line
 * *9.0 h on this visit · 10:15 on the clock* with its internal cost — the
 * server's arithmetic for what is typed (`POST /visits/:id/time/preview`) —
 * and **Why** (required). → `POST /visits/:id/time`, `CrewPort.addTime`:
 * `punch_events` corrections (`source = 'office'`, `entered_by`, the reason)
 * that make the intervals — the same rows the crew app writes.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { AddTimeInput, AddTimePreview, PunchPlace, PunchPlaceInput, TimeRead, VisitRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Popover } from '../components/overlays.js'
import { ADD_TIME, COMMON } from './copy.js'
import { dayText, hm, hours1, money } from './format.js'
import { Face } from './face.js'
import { useVisitWrite, visitPath, type VisitWriteResult } from './api.js'
import { VisitButton, VisitPopup, VisitTile, VISIT_LAYER, VISIT_LAYER_OVER } from './sheet.js'
import { ChoiceField, FieldLabel, instantAt, parseClock, TimeBox } from './time-money-parts.js'
import { InternalChip } from './internal-chip.js'

type Edge = 'clockIn' | 'arrived' | 'left' | 'clockOut' | 'lunchFrom' | 'lunchTo'

const EDGES: Edge[] = ['clockIn', 'arrived', 'left', 'clockOut', 'lunchFrom', 'lunchTo']

const LABELS: Record<Edge, string> = {
  clockIn: ADD_TIME.clockedIn,
  arrived: ADD_TIME.arrived,
  left: ADD_TIME.left,
  clockOut: ADD_TIME.clockedOut,
  lunchFrom: ADD_TIME.lunchFrom,
  lunchTo: ADD_TIME.lunchTo,
}

/** A place's key in the popup's state — `yard:<id>`, `vendor:<id>`, `site`. */
function keyOf(place: PunchPlace): string | null {
  if (place.kind === 'yard') return place.yard ? `yard:${place.yard.id}` : null
  if (place.kind === 'vendor') return `vendor:${place.vendor.id}`
  return 'site'
}

function inputOf(place: PunchPlace): PunchPlaceInput | null {
  if (place.kind === 'yard') return place.yard ? { kind: 'yard', yardId: place.yard.id } : null
  if (place.kind === 'vendor') return { kind: 'vendor', vendorId: place.vendor.id }
  return { kind: 'site' }
}

/** *Keystone Equipment Rental* · *Harrisburg yard* · the property for the site. */
function nameOf(place: PunchPlace, read: VisitRead): string {
  if (place.kind === 'yard') return place.yard?.name ?? ''
  if (place.kind === 'vendor') return place.vendor.name
  return read.header.meta.property?.name ?? ''
}

export function AddTimePopup({ visitId, read, time, phone, onClose, onAdded }: {
  visitId: string
  read: VisitRead
  time: TimeRead
  phone: boolean
  onClose: () => void
  onAdded: (result: VisitWriteResult) => void
}) {
  const write = useVisitWrite(visitId)
  const places = useMemo(() => time.add.places.filter((place) => keyOf(place) !== null), [time])
  const firstYard = places.find((place) => place.kind === 'yard') ?? places[0] ?? null
  const [userId, setUserId] = useState<string | null>(null)
  const [day, setDay] = useState<string>(() => String((time.days.find((d) => d.relative === 'today') ?? time.days[0])?.index ?? ''))
  const [times, setTimes] = useState<Record<Edge, string>>({ clockIn: '', arrived: '', left: '', clockOut: '', lunchFrom: '', lunchTo: '' })
  const [inAt, setInAt] = useState<string | null>(firstYard ? keyOf(firstYard) : null)
  const [outAt, setOutAt] = useState<string | null>(firstYard ? keyOf(firstYard) : null)
  const [why, setWhy] = useState('')
  const [preview, setPreview] = useState<AddTimePreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const person = time.add.people.find((candidate) => candidate.id === userId) ?? null
  const dayRef = time.days.find((candidate) => String(candidate.index) === day) ?? null
  const placeBy = (key: string | null) => places.find((place) => keyOf(place) === key) ?? null

  /** What is typed, as the request — or null while a required time or place is missing or out of order. */
  const fields = useMemo(() => {
    if (!person || !dayRef) return null
    const minute = Object.fromEntries(EDGES.map((edge) => [edge, parseClock(times[edge])])) as Record<Edge, number | null>
    if (minute.clockIn === null || minute.arrived === null || minute.left === null || minute.clockOut === null) return null
    if ((minute.lunchFrom === null) !== (minute.lunchTo === null)) return null
    const clockInPlace = placeBy(inAt)
    const clockOutPlace = placeBy(outAt)
    const inPlace = clockInPlace ? inputOf(clockInPlace) : null
    const outPlace = clockOutPlace ? inputOf(clockOutPlace) : null
    if (!inPlace || !outPlace) return null
    const stamp = (value: number) => instantAt(read.timeZone, dayRef.date, value)
    const order = [minute.clockIn, minute.arrived, ...(minute.lunchFrom !== null ? [minute.lunchFrom, minute.lunchTo!] : []), minute.left, minute.clockOut]
    if (order.some((value, index) => index > 0 && value < order[index - 1]!)) return null
    return {
      userId: person.id,
      day: dayRef.index,
      clockInAt: stamp(minute.clockIn),
      clockInPlace: inPlace,
      arrivedAt: stamp(minute.arrived),
      leftAt: stamp(minute.left),
      clockOutAt: stamp(minute.clockOut),
      clockOutPlace: outPlace,
      lunchFrom: minute.lunchFrom !== null ? stamp(minute.lunchFrom) : null,
      lunchTo: minute.lunchTo !== null ? stamp(minute.lunchTo) : null,
    }
  }, [person, dayRef, times, inAt, outAt, places, read.timeZone])

  /* The line under the times is the server's: hours on the visit, on the clock, and the cost (§6.5). */
  const asked = useRef(0)
  useEffect(() => {
    if (!fields) { setPreview(null); return }
    const ticket = ++asked.current
    const timer = window.setTimeout(() => {
      request<AddTimePreview>(visitPath(read.id, '/time/preview'), { method: 'POST', body: fields })
        .then((answer) => { if (ticket === asked.current) setPreview(answer) })
        .catch(() => { if (ticket === asked.current) setPreview(null) })
    }, 200)
    return () => window.clearTimeout(timer)
  }, [fields, read.id])

  const ready = fields !== null && why.trim().length > 0 && !busy

  async function add() {
    if (!fields || !ready) return
    setBusy(true)
    setProblem(null)
    const body: AddTimeInput = { ...fields, reason: why.trim() }
    try {
      onAdded(await write('POST', '/time', body, { tab: 'time' }))
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
      setBusy(false)
    }
  }

  const size = phone ? 'phone' : 'popup'
  const timeBox = (edge: Edge, help?: ReactNode) => (
    <div className="min-w-0">
      <FieldLabel htmlFor={`add-time-${edge}`}>{LABELS[edge]}</FieldLabel>
      <TimeBox id={`add-time-${edge}`} label={LABELS[edge]} value={times[edge]} phone={phone}
        onChange={(text) => setTimes((was) => ({ ...was, [edge]: text }))} />
      {help}
    </div>
  )

  return (
    <VisitPopup
      width={520}
      phone={phone}
      title={ADD_TIME.title}
      sub={ADD_TIME.sub(read.reference, read.header.name)}
      mark={<VisitTile glyph="clock" tone="teal" size={34} />}
      onClose={onClose}
      {...(person ? { note: ADD_TIME.footer(person.name, time.add.by.name) } : {})}
      noteSize={11.5}
      layer={VISIT_LAYER}
      asDrawn="vp-18 E"
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{ADD_TIME.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" disabled={!ready} onClick={() => void add()}>{ADD_TIME.add}</VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className={phone ? 'flex flex-col gap-3' : 'grid grid-cols-2 gap-x-3.5 gap-y-3'}>
          <ChoiceField
            label={ADD_TIME.person}
            required
            value={userId}
            choices={time.add.people.map((candidate) => ({
              value: candidate.id, label: candidate.name, lead: <Face person={candidate} size={22} />,
            }))}
            onChange={setUserId}
            phone={phone}
            layer={VISIT_LAYER_OVER}
          />
          <ChoiceField
            label={ADD_TIME.day}
            required
            value={day}
            chevron={false}
            choices={time.days.map((candidate) => ({
              value: String(candidate.index),
              label: ADD_TIME.dayValue(String(candidate.index), dayText(candidate.date, 'date')),
            }))}
            onChange={setDay}
            phone={phone}
            layer={VISIT_LAYER_OVER}
          />
        </div>

        <div className={cn('grid', phone ? 'grid-cols-2 gap-3' : 'grid-cols-4 gap-2.5')}>
          {timeBox('clockIn', <PlacePick read={read} places={places} value={inAt} onChange={setInAt} phone={phone} />)}
          {timeBox('arrived')}
          {timeBox('left')}
          {timeBox('clockOut', <PlacePick read={read} places={places} value={outAt} onChange={setOutAt} phone={phone} />)}
        </div>

        <div className={cn('grid grid-cols-2', phone ? 'gap-3' : 'gap-x-3.5 gap-y-3')}>
          {timeBox('lunchFrom')}
          {timeBox('lunchTo')}
        </div>

        {preview && (
          <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-line-faint bg-surface-alt px-3 py-2.5 text-[12.5px]"
            data-add-time-line="">
            <b>{COMMON.h(hours1(preview.onVisit.hours))}</b>
            <span>{ADD_TIME.onThisVisit}</span>
            <span className="text-text-faint">·</span>
            <b>{hm(preview.onClock.minutes)}</b>
            <span>{ADD_TIME.onTheClock}</span>
            <span className="flex-1" />
            {preview.internal && (
              <span className="inline-flex items-center gap-1.5 rounded-[7px] border border-line bg-surface-internal px-2 py-[3px]">
                <InternalChip />
                <b>{money(preview.internal.cost)}</b>
              </span>
            )}
          </div>
        )}

        <div className="min-w-0">
          <FieldLabel htmlFor="add-time-why" required>{ADD_TIME.why}</FieldLabel>
          <textarea
            id="add-time-why"
            value={why}
            onChange={(event) => setWhy(event.target.value)}
            className="block h-[52px] w-full resize-none rounded-button tabular-nums border border-line bg-surface px-3 py-2.5 text-[13px]
                       leading-normal text-text outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]"
          />
        </div>

        {problem && <p className="m-0 text-[12.5px] text-danger">{problem}</p>}
      </div>
    </VisitPopup>
  )
}

/**
 * *at Keystone Equipment Rental* under **Clocked in** and *at Harrisburg yard*
 * under **Clocked out** (Board 18 E) — where the punch was; a press offers the
 * person's places that day (the yard, a stop, the site).
 */
function PlacePick({ read, places, value, onChange, phone }: {
  read: VisitRead
  places: PunchPlace[]
  value: string | null
  onChange: (key: string) => void
  phone: boolean
}) {
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const chosen = places.find((place) => keyOf(place) === value) ?? null
  const label = chosen ? ADD_TIME.at(nameOf(chosen, read)) : ''
  if (phone) {
    return (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {places.map((place) => {
          const key = keyOf(place)!
          return (
            <button key={key} type="button" onClick={() => onChange(key)} aria-pressed={key === value}
              className={cn('inline-flex min-h-11 items-center rounded-full border px-3 text-[12.5px] font-semibold',
                key === value ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text-muted')}>
              {ADD_TIME.at(nameOf(place, read))}
            </button>
          )
        })}
      </div>
    )
  }
  return (
    <>
      <button
        ref={setAnchor}
        type="button"
        onClick={() => setOpen((was) => !was)}
        className="mt-1 block text-left text-[11px] leading-[1.4] text-text-faint hover:text-text-muted"
      >
        {label}
      </button>
      <Popover open={open} onClose={() => setOpen(false)} label={label} anchor={anchor} width={240}
        layer={VISIT_LAYER_OVER} className="py-1">
        {places.map((place) => {
          const key = keyOf(place)!
          return (
            <button key={key} type="button" onClick={() => { onChange(key); setOpen(false) }}
              className={cn('flex h-9 w-full items-center px-3 text-left text-[13px]',
                key === value ? 'bg-accent-soft font-semibold text-accent-dark' : 'text-text hover:bg-hover')}>
              {ADD_TIME.at(nameOf(place, read))}
            </button>
          )
        })}
      </Popover>
    </>
  )
}
